import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    XMarkIcon,
    ArrowUpTrayIcon,
    DocumentIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    BriefcaseIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { getErrorMessage } from '../../utils/errorHelpers';

interface ImportPreviewResult {
    totalRows: number;
    validCount: number;
    duplicateCount: number;
    invalidCount: number;
    employees: ImportEmployeePreview[];
    duplicateEmails: string[];
    missingLocations: string[];
    missingDepartments: string[];
    missingPayrollCompanies: string[];
    hasMissingEntities: boolean;
}

interface ImportEmployeePreview {
    employeeNumber: string;
    fullName: string;
    designation?: string;
    workEmail?: string;
    locationName?: string;
    departmentName?: string;
    payrollCompanyName?: string;
    employmentDate?: string;
    dateOfBirth?: string;
    gender?: string;
    rowNumber: number;
    isValid: boolean;
    validationErrors: string[];
}

interface ImportExecuteResult {
    importedCount: number;
    skippedCount: number;
    createdLocations: number;
    createdDepartments: number;
    createdPayrollCompanies: number;
    errors: string[];
    success: boolean;
}

interface ImportEmployeesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type Step = 'upload' | 'preview' | 'confirm' | 'result';

export default function ImportEmployeesModal({ isOpen, onClose, onSuccess }: ImportEmployeesModalProps) {
    const [step, setStep] = useState<Step>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [previewResult, setPreviewResult] = useState<ImportPreviewResult | null>(null);
    const [executeResult, setExecuteResult] = useState<ImportExecuteResult | null>(null);
    const [userConfirmedMissing, setUserConfirmedMissing] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const resetModal = () => {
        setStep('upload');
        setFile(null);
        setPreviewResult(null);
        setExecuteResult(null);
        setUserConfirmedMissing(false);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const extension = selectedFile.name.toLowerCase().split('.').pop();
            if (extension !== 'xlsx' && extension !== 'xls') {
                toast.error('Please select an Excel file (.xlsx or .xls)');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            const extension = droppedFile.name.toLowerCase().split('.').pop();
            if (extension !== 'xlsx' && extension !== 'xls') {
                toast.error('Please select an Excel file (.xlsx or .xls)');
                return;
            }
            setFile(droppedFile);
        }
    };

    const handlePreview = async () => {
        if (!file) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await createAuthenticatedFetch().fetch(
                `${API_CONFIG.baseUrl}/api/employees/import/preview`,
                {
                    method: 'POST',
                    body: formData
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(getErrorMessage(error, 'Failed to preview import'));
            }

            const result: ImportPreviewResult = await response.json();
            setPreviewResult(result);
            setStep('preview');
        } catch (error: unknown) {
            toast.error(getErrorMessage(error, 'Failed to preview import'));
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteImport = async () => {
        if (!file) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('createMissingEntities', 'true');

            const response = await createAuthenticatedFetch().fetch(
                `${API_CONFIG.baseUrl}/api/employees/import/execute?createMissingEntities=true`,
                {
                    method: 'POST',
                    body: formData
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(getErrorMessage(error, 'Failed to execute import'));
            }

            const result: ImportExecuteResult = await response.json();
            setExecuteResult(result);
            setStep('result');

            if (result.success && result.importedCount > 0) {
                toast.success(`Successfully imported ${result.importedCount} employees`);
            }
        } catch (error: unknown) {
            toast.error(getErrorMessage(error, 'Failed to execute import'));
        } finally {
            setLoading(false);
        }
    };

    const handleFinish = () => {
        resetModal();
        onSuccess();
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Import Employees</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {step === 'upload' && 'Upload an Excel file with employee data'}
                            {step === 'preview' && 'Review the import preview'}
                            {step === 'confirm' && 'Confirm missing entities creation'}
                            {step === 'result' && 'Import completed'}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Step: Upload */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                                    file
                                        ? 'border-green-400 bg-green-50'
                                        : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                {file ? (
                                    <>
                                        <DocumentIcon className="mx-auto h-12 w-12 text-green-500" />
                                        <p className="mt-4 text-lg font-medium text-gray-900">{file.name}</p>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                        <p className="mt-4 text-sm text-green-600">
                                            Click to select a different file
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <p className="mt-4 text-lg font-medium text-gray-900">
                                            Drop your Excel file here
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                            or click to browse
                                        </p>
                                        <p className="mt-4 text-xs text-gray-400">
                                            Supported formats: .xlsx, .xls
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-medium text-blue-900 mb-2">Expected Columns</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                                    <div>1. Empl ID</div>
                                    <div>2. Emp Name</div>
                                    <div>3. Position Title</div>
                                    <div>4. Payroll Company</div>
                                    <div>5. Location</div>
                                    <div>6. First Joined Date (ignored)</div>
                                    <div>7. Hire Date</div>
                                    <div>8. Rehire Date (ignored)</div>
                                    <div>9. Termination Date (ignored)</div>
                                    <div>10. Department</div>
                                    <div>11. Email Address</div>
                                    <div>12. Date of Birth</div>
                                    <div>13. Gender</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step: Preview */}
                    {step === 'preview' && previewResult && (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-gray-900">{previewResult.totalRows}</div>
                                    <div className="text-sm text-gray-500">Total Rows</div>
                                </div>
                                <div className="bg-green-50 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-green-600">{previewResult.validCount}</div>
                                    <div className="text-sm text-green-600">Valid</div>
                                </div>
                                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-yellow-600">{previewResult.duplicateCount}</div>
                                    <div className="text-sm text-yellow-600">Duplicates</div>
                                </div>
                                <div className="bg-red-50 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-red-600">{previewResult.invalidCount}</div>
                                    <div className="text-sm text-red-600">Invalid</div>
                                </div>
                            </div>

                            {/* Missing Entities Warning */}
                            {previewResult.hasMissingEntities && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                                        <div className="flex-1">
                                            <h4 className="font-medium text-amber-900">
                                                Missing Entities Detected
                                            </h4>
                                            <p className="text-sm text-amber-700 mt-1">
                                                The following entities don't exist in the system. They will be created automatically if you proceed.
                                            </p>

                                            <div className="mt-4 space-y-3">
                                                {previewResult.missingLocations.length > 0 && (
                                                    <div className="flex items-start">
                                                        <BuildingOfficeIcon className="h-4 w-4 text-amber-600 mr-2 mt-0.5" />
                                                        <div>
                                                            <span className="font-medium text-amber-800">Locations: </span>
                                                            <span className="text-amber-700">
                                                                {previewResult.missingLocations.join(', ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {previewResult.missingDepartments.length > 0 && (
                                                    <div className="flex items-start">
                                                        <UserGroupIcon className="h-4 w-4 text-amber-600 mr-2 mt-0.5" />
                                                        <div>
                                                            <span className="font-medium text-amber-800">Departments: </span>
                                                            <span className="text-amber-700">
                                                                {previewResult.missingDepartments.join(', ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {previewResult.missingPayrollCompanies.length > 0 && (
                                                    <div className="flex items-start">
                                                        <BriefcaseIcon className="h-4 w-4 text-amber-600 mr-2 mt-0.5" />
                                                        <div>
                                                            <span className="font-medium text-amber-800">Payroll Companies: </span>
                                                            <span className="text-amber-700">
                                                                {previewResult.missingPayrollCompanies.join(', ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <label className="flex items-center mt-4 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={userConfirmedMissing}
                                                    onChange={(e) => setUserConfirmedMissing(e.target.checked)}
                                                    className="h-4 w-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                                                />
                                                <span className="ml-2 text-sm text-amber-800">
                                                    I confirm to create these missing entities automatically
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Duplicate Emails */}
                            {previewResult.duplicateEmails.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <h4 className="font-medium text-yellow-900 flex items-center">
                                        <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                        Duplicate Emails (will be skipped)
                                    </h4>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        {previewResult.duplicateEmails.slice(0, 10).join(', ')}
                                        {previewResult.duplicateEmails.length > 10 && ` and ${previewResult.duplicateEmails.length - 10} more...`}
                                    </div>
                                </div>
                            )}

                            {/* Employee Preview Table */}
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {previewResult.employees.map((emp) => (
                                                <tr key={emp.rowNumber} className={emp.isValid ? '' : 'bg-red-50'}>
                                                    <td className="px-4 py-2 text-sm text-gray-500">{emp.rowNumber}</td>
                                                    <td className="px-4 py-2">
                                                        {emp.isValid ? (
                                                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                                        ) : (
                                                            <div className="flex items-center">
                                                                <XCircleIcon className="h-5 w-5 text-red-500" />
                                                                <span className="ml-1 text-xs text-red-600" title={emp.validationErrors.join(', ')}>
                                                                    {emp.validationErrors[0]}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900">{emp.fullName}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-500">{emp.workEmail}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-500">{emp.locationName}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-500">{emp.departmentName}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step: Result */}
                    {step === 'result' && executeResult && (
                        <div className="text-center py-8">
                            {executeResult.success ? (
                                <>
                                    <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
                                    <h3 className="mt-4 text-xl font-semibold text-gray-900">
                                        Import Completed Successfully
                                    </h3>
                                </>
                            ) : (
                                <>
                                    <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-yellow-500" />
                                    <h3 className="mt-4 text-xl font-semibold text-gray-900">
                                        Import Completed with Issues
                                    </h3>
                                </>
                            )}

                            <div className="mt-6 grid grid-cols-2 gap-4 max-w-md mx-auto">
                                <div className="bg-green-50 rounded-lg p-4">
                                    <div className="text-2xl font-bold text-green-600">{executeResult.importedCount}</div>
                                    <div className="text-sm text-green-600">Imported</div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="text-2xl font-bold text-gray-600">{executeResult.skippedCount}</div>
                                    <div className="text-sm text-gray-600">Skipped</div>
                                </div>
                            </div>

                            {(executeResult.createdLocations > 0 || executeResult.createdDepartments > 0 || executeResult.createdPayrollCompanies > 0) && (
                                <div className="mt-6 bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
                                    <h4 className="font-medium text-blue-900 mb-2">Created Entities</h4>
                                    <div className="text-sm text-blue-700 space-y-1">
                                        {executeResult.createdLocations > 0 && (
                                            <div>{executeResult.createdLocations} Location(s)</div>
                                        )}
                                        {executeResult.createdDepartments > 0 && (
                                            <div>{executeResult.createdDepartments} Department(s)</div>
                                        )}
                                        {executeResult.createdPayrollCompanies > 0 && (
                                            <div>{executeResult.createdPayrollCompanies} Payroll Company(ies)</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {executeResult.errors.length > 0 && (
                                <div className="mt-6 bg-red-50 rounded-lg p-4 max-w-md mx-auto text-left">
                                    <h4 className="font-medium text-red-900 mb-2">Errors</h4>
                                    <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                                        {executeResult.errors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
                    {step === 'upload' && (
                        <>
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePreview}
                                disabled={!file || loading}
                                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Processing...
                                    </>
                                ) : (
                                    'Preview Import'
                                )}
                            </button>
                        </>
                    )}

                    {step === 'preview' && (
                        <>
                            <button
                                onClick={() => setStep('upload')}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleExecuteImport}
                                disabled={
                                    loading ||
                                    previewResult?.validCount === 0 ||
                                    (previewResult?.hasMissingEntities && !userConfirmedMissing)
                                }
                                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Importing...
                                    </>
                                ) : (
                                    `Import ${previewResult?.validCount || 0} Employees`
                                )}
                            </button>
                        </>
                    )}

                    {step === 'result' && (
                        <button
                            onClick={handleFinish}
                            className="ml-auto px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
